import React, { useState, useEffect, Suspense, lazy } from 'react';
import { 
  Box, 
  Container,
  useMediaQuery,
  useTheme,
  Toolbar,
  CircularProgress,
  Typography
} from '@mui/material';
import { Routes, Route } from 'react-router-dom';
import { useFixMobileLayout } from '../hooks/useFixMobileLayout';

// Import novos componentes modernos
import ModernHeader from '../components/layout/ModernHeader';
import ModernSidebar from '../components/layout/ModernSidebar';
import PageTransition from '../components/layout/PageTransition';
import MobileLayoutFix from '../components/layout/MobileLayoutFix';

// Dashboard sub-pages (lazy loaded)
const DashboardHome = lazy(() => import('./dashboard/DashboardHome'));
const SalesAnalytics = lazy(() => import('./dashboard/SalesAnalytics'));
const StorePerformance = lazy(() => import('./dashboard/StorePerformance'));
// Phase 5 - Advanced Dashboards
const VendedorPerformance = lazy(() => import('./dashboard/VendedorPerformance'));
const VendasExternas = lazy(() => import('./dashboard/VendasExternas'));
const ProductAnalytics = lazy(() => import('./dashboard/ProductAnalytics'));
// Store Details Page
const StoreDetails = lazy(() => import('./dashboard/StoreDetails'));
// Product Details Page
const ProductDetails = lazy(() => import('./dashboard/ProductDetails'));
// Vendor Details Page
const VendedorDetails = lazy(() => import('./dashboard/VendedorDetails'));
// Monthly Analytics Page
const MonthlyAnalytics = lazy(() => import('./dashboard/MonthlyAnalytics'));

const drawerWidth = 240;

const Dashboard: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Aplicar fix de layout mobile
  useFixMobileLayout();

  // Close drawer on mobile by default
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  // Fix layout issues after navigation, especially on mobile
  useEffect(() => {
    // Pequeno delay para garantir que o DOM está pronto
    const timer = setTimeout(() => {
      setIsLayoutReady(true);
      // Força recálculo do layout
      window.dispatchEvent(new Event('resize'));
      
      // Força scroll para o topo em mobile
      if (isMobile) {
        window.scrollTo(0, 0);
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }
    }, 50);
    
    return () => clearTimeout(timer);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const LoadingFallback = () => (
    <Box 
      display="flex" 
      flexDirection="column"
      justifyContent="center" 
      alignItems="center" 
      minHeight="200px"
      gap={2}
    >
      <CircularProgress size={40} />
      <Typography variant="body2" color="text.secondary">
        Carregando...
      </Typography>
    </Box>
  );

  return (
    <MobileLayoutFix>
      <Box sx={{ 
        display: 'flex', 
        height: '100vh', 
        overflow: 'hidden', 
        position: 'relative',
        opacity: isLayoutReady ? 1 : 0,
        transition: 'opacity 0.2s ease-in-out'
      }}>
      {/* Header moderno */}
      <ModernHeader
        drawerWidth={drawerWidth}
        open={open}
        onDrawerToggle={handleDrawerToggle}
      />

      {/* Overlay escuro para mobile quando drawer está aberto */}
      {isMobile && open && (
        <Box
          onClick={handleDrawerClose}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: theme.zIndex.drawer - 1,
            cursor: 'pointer'
          }}
        />
      )}

      {/* Sidebar moderna */}
      <ModernSidebar
        drawerWidth={drawerWidth}
        open={open}
        onClose={handleDrawerClose}
      />

      {/* Conteúdo principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
          overflow: 'auto',
          // Header fixo já gerencia o espaço da sidebar
        }}
      >
        {/* Espaçamento para o header fixo */}
        <Toolbar sx={{ 
          minHeight: { xs: '56px', sm: '64px' },
          '@media (min-width: 0px)': {
            minHeight: '56px',
          },
          '@media (min-width: 600px)': {
            minHeight: '64px',
          },
          flexShrink: 0,
          visibility: 'hidden', // Mantém o espaço mas não mostra
          pointerEvents: 'none'
        }} />
        
        {/* Container do conteúdo */}
        <Container 
          maxWidth="xl" 
          sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            py: 2,
            px: 0,
            minHeight: 0,
            margin: 0,
            maxWidth: 'none',
            // Adicionar padding-top extra em mobile para evitar sobreposição
            pt: 2
          }}
        >
          <PageTransition>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/" element={<DashboardHome />} />
                <Route path="/sales" element={<SalesAnalytics />} />
                <Route path="/stores" element={<StorePerformance />} />
                {/* Phase 5 - Advanced Dashboards */}
                <Route path="/vendedores" element={<VendedorPerformance />} />
                <Route path="/vendas-externas" element={<VendasExternas />} />
                <Route path="/produtos" element={<ProductAnalytics />} />
                {/* Store Details Route */}
                <Route path="/stores/:lojaId" element={<StoreDetails />} />
                {/* Product Details Route */}
                <Route path="/produtos/:produtoId" element={<ProductDetails />} />
                {/* Vendor Details Route */}
                <Route path="/vendedores/:vendedorId" element={<VendedorDetails />} />
                {/* Monthly Analytics Route */}
                <Route path="/monthly/:month" element={<MonthlyAnalytics />} />
              </Routes>
            </Suspense>
          </PageTransition>
        </Container>
      </Box>
    </Box>
    </MobileLayoutFix>
  );
};

export default Dashboard;
